import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  Typography,
  alpha,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PushPinIcon from "@mui/icons-material/PushPin";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";
import ComponentLoading from "../../components/App/ComponentLoading";
import PdfSettingsDialog from "../../components/Schedules/Others/Pdf/PdfSettingsDialog";
import { IRAS_TABLE_SPACING_DISPLAY_PRESETS } from "../../components/MainTaxComputation/Pdf/iras/tableSpacing";
import {
  getUserPreference,
  updateGeneralPreference,
  updatePdfPreference,
} from "../../contexts/services/userPreferenceServices";
import { useErrorDialog } from "../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../contexts/SuccessDialogContext";

const DEFAULT_PREFERENCE = {
  general: {
    header: {
      stickyPin: true,
    },
  },
  pdf: {
    settings: {
      pdfDensity: "default",
      pdfSpacingOptions: null,
    },
  },
};

const PDF_DENSITY_OPTIONS = {
  options: [
    { value: "default", label: "Default" },
    { value: "compact", label: "Compact" },
    { value: "customize", label: "Customize" },
  ],
};

const clonePreference = (value) => JSON.parse(JSON.stringify(value));

const isSamePreferenceValue = (left, right) =>
  JSON.stringify(left) === JSON.stringify(right);

const mergePreference = (preference) => ({
  general: {
    ...DEFAULT_PREFERENCE.general,
    ...(preference?.general || {}),
    header: {
      ...DEFAULT_PREFERENCE.general.header,
      ...(preference?.general?.header || {}),
    },
  },
  pdf: {
    ...DEFAULT_PREFERENCE.pdf,
    ...(preference?.pdf || {}),
    settings: {
      ...DEFAULT_PREFERENCE.pdf.settings,
      ...(preference?.pdf?.settings || {}),
    },
  },
});

const SectionPanel = ({ action, children, description, icon, title }) => (
  <Paper
    elevation={0}
    sx={{
      border: 1,
      borderColor: "divider",
      borderRadius: 2,
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        alignItems: { xs: "flex-start", sm: "center" },
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.035),
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.5,
        justifyContent: "space-between",
        px: 2.5,
        py: 2,
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          gap: 1.5,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            borderRadius: 1,
            color: "primary.main",
            display: "flex",
            flexShrink: 0,
            height: 36,
            justifyContent: "center",
            width: 36,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {description}
          </Typography>
        </Box>
      </Box>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Box>
    <Divider />
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Paper>
);

const PreferenceRow = ({ action, description, label, value }) => (
  <Box
    sx={{
      alignItems: { xs: "flex-start", sm: "center" },
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      gap: 2,
      justifyContent: "space-between",
      py: 1,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography variant='body2' sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        {description}
      </Typography>
    </Box>
    <Stack
      direction='row'
      spacing={1.5}
      alignItems='center'
      sx={{ flexShrink: 0 }}
    >
      {value}
      {action}
    </Stack>
  </Box>
);

const DENSITY_LABEL_MAP = {
  compact: "Compact",
  customize: "Custom",
  default: "Default",
};

const DENSITY_COLOR_MAP = {
  default: "primary",
  compact: "secondary",
  customize: "success",
};

const Preferences = () => {
  const [preference, setPreference] = useState(DEFAULT_PREFERENCE);
  const [draftPreference, setDraftPreference] = useState(DEFAULT_PREFERENCE);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [pdfSettingsOpen, setPdfSettingsOpen] = useState(false);
  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const pdfSettings = preference.pdf.settings;
  const generalHasChanges = !isSamePreferenceValue(
    preference.general,
    draftPreference.general
  );

  useEffect(() => {
    let isMounted = true;

    const loadPreference = async () => {
      try {
        setLoading(true);
        const response = await getUserPreference();
        const nextPreference = mergePreference(response?.data || response);
        if (isMounted) {
          setPreference(nextPreference);
          setDraftPreference(clonePreference(nextPreference));
        }
      } catch (error) {
        if (isMounted)
          handleError(error?.message || "Failed to load preferences.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadPreference();

    return () => {
      isMounted = false;
    };
  }, [handleError]);

  const saveGeneralPreference = async () => {
    setSavingKey("general");

    try {
      const response = await updateGeneralPreference({
        value: draftPreference.general,
      });
      const responseData = response?.data || response;
      const nextGeneral = responseData?.general || draftPreference.general;
      setPreference((prev) =>
        mergePreference({ ...prev, general: nextGeneral })
      );
      setDraftPreference((prev) =>
        mergePreference({ ...prev, general: nextGeneral })
      );
      handleSuccess("General preferences saved.");
    } catch (error) {
      handleError(error?.message || "Failed to save preferences.");
    } finally {
      setSavingKey("");
    }
  };

  const savePdfPreference = async (settings) => {
    try {
      const nextPdf = {
        ...preference.pdf,
        settings: {
          ...preference.pdf.settings,
          pdfDensity: settings.pdfDensity,
          pdfSpacingOptions:
            settings.pdfDensity === "customize"
              ? settings.pdfSpacingOptions
              : null,
        },
      };
      const response = await updatePdfPreference({
        value: nextPdf,
      });
      const responseData = response?.data || response;
      const savedPdf = responseData?.pdf || nextPdf;
      setPreference((prev) => mergePreference({ ...prev, pdf: savedPdf }));
      setDraftPreference((prev) => mergePreference({ ...prev, pdf: savedPdf }));
      handleSuccess("PDF preferences saved.");
    } catch (error) {
      handleError(error?.message || "Failed to save PDF preferences.");
      throw error;
    }
  };

  const handleGeneralStickyPinChange = (checked) => {
    setDraftPreference((prev) => ({
      ...prev,
      general: {
        ...prev.general,
        header: {
          ...prev.general.header,
          stickyPin: checked,
        },
      },
    }));
  };

  if (loading) return <ComponentLoading />;

  return (
    <Box sx={{ width: "100%" }}>
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography variant='h5' sx={{ fontWeight: 700 }}>
          Preferences
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Manage personal defaults used across your workspace.
        </Typography>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <SectionPanel
            title='General'
            description='Personal behaviour preferences for the application shell.'
            icon={<PushPinIcon fontSize='small' />}
            action={
              <Button
                disabled={!generalHasChanges || savingKey === "general"}
                onClick={saveGeneralPreference}
                variant='contained'
              >
                Save General
              </Button>
            }
          >
            <PreferenceRow
              label='Sticky document header'
              description='Keep schedule and computation headers pinned while you scroll.'
              value={
                <Chip
                  size='small'
                  label={
                    draftPreference.general.header.stickyPin ? "On" : "Off"
                  }
                  color={
                    draftPreference.general.header.stickyPin
                      ? "primary"
                      : "default"
                  }
                  variant={
                    draftPreference.general.header.stickyPin
                      ? "filled"
                      : "outlined"
                  }
                />
              }
              action={
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(
                        draftPreference.general.header.stickyPin
                      )}
                      disabled={savingKey === "general"}
                      onChange={(event) =>
                        handleGeneralStickyPinChange(event.target.checked)
                      }
                    />
                  }
                  label=''
                  sx={{ mr: 0 }}
                />
              }
            />
          </SectionPanel>
        </Grid>

        <Grid item xs={12}>
          <SectionPanel
            title='PDF'
            description='Default PDF display density and spacing preferences. (Currently only applicable to Single YA Trading type tax comp PDF generation.)'
            icon={<PictureAsPdfIcon fontSize='small' />}
          >
            <Stack spacing={2}>
              <PreferenceRow
                label='PDF spacing mode'
                description='Default density used by PDF generation controls.'
                value={
                  <Chip
                    size='small'
                    icon={<TuneIcon fontSize='small' />}
                    label={`Current Mode: ${DENSITY_LABEL_MAP[pdfSettings.pdfDensity] || pdfSettings.pdfDensity}`}
                    color={
                      DENSITY_COLOR_MAP[pdfSettings.pdfDensity] || "default"
                    }
                    variant={
                      pdfSettings.pdfDensity === "customize"
                        ? "filled"
                        : "outlined"
                    }
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: (theme) =>
                          alpha(theme.palette.primary.main, 0.08),
                      },
                      "& .MuiChip-deleteIcon": {
                        fontSize: "1rem",
                      },
                    }}
                  />
                }
                action={
                  <Button
                    onClick={() => setPdfSettingsOpen(true)}
                    size='small'
                    startIcon={<SettingsIcon fontSize='small' />}
                    variant='outlined'
                  >
                    Edit PDF Settings
                  </Button>
                }
              />
            </Stack>
          </SectionPanel>
        </Grid>
      </Grid>
      <PdfSettingsDialog
        densityOptions={PDF_DENSITY_OPTIONS}
        onClose={() => setPdfSettingsOpen(false)}
        onSave={savePdfPreference}
        open={pdfSettingsOpen}
        selectedDensity={pdfSettings.pdfDensity}
        spacingOptions={pdfSettings.pdfSpacingOptions}
        spacingPresets={IRAS_TABLE_SPACING_DISPLAY_PRESETS}
      />
    </Box>
  );
};

export default Preferences;
